import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import { translate } from 'services/i18nService';
import createMessagingService from 'services/MessagingService/MessagingService';
import {
  ALL_NOTES_RESPONSE,
  BROADCASTER_NOTE_DELETED,
  CLOSE_BROADCASTER_SEARCH,
  NOTE_UPDATED_OR_CREATED,
  NOTES_LIST_RESPONSE,
  NEW_NOTE_CREATED,
  RESET_SUCCESS_NOTE_VIEW,
  SET_BROADCASTER_NOTES_SEARCH_ERROR_VALUE,
  SET_NEW_NOTE_VIEW,
  SINGLE_NOTE_RESPONSE,
  UPDATE_ALL_NOTES,
  UPDATE_ALL_NOTES_COUNT,
} from 'services/MessagingService/EventNames';
import ModalService from 'services/ModalService';
import NotificationService from 'services/NotificationService';
import { RESPONSE_STATUSES } from 'utils/consts';
import parseCatch from 'utils/parseCatch';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);

const USER_NOTES_URL = `${BASE_URL}/rest/v1.0/users/{broadcasterUsername}/notes`;
const USER_NOTES_SINGLE_NOTE__URL = `${BASE_URL}/rest/v1.0/users/{broadcasterUsername}/notes/{targetUsername}`;

export class BroadcasterNotesService {
  /**
   * Constructor of @BroadcasterNotesService class.
   * @param {function} messagingService a MessagingService creator
   * Bind methods to messaging service events
   */
  constructor(messagingService) {
    this.messagingService = messagingService();
  }

  /**
   * @async
   * Method for fetching all notes
   * @param {string} broadcasterName
   * @emits NOTES_LIST_RESPONSE sends all notes object
   * @returns promise
   */
  getNotesList = (broadcasterName, offset, activeColorValue) => {
    const URL = USER_NOTES_URL.replace('{broadcasterUsername}', broadcasterName);

    return ApiService.get(URL, { params: { offset: offset ? offset : 0, limit: 12, colors: activeColorValue } })
      .then((response) => {
        this.messagingService.emit(NOTES_LIST_RESPONSE, response.data);
      })
      .catch((error) => parseCatch(error));
  };

  /**
   * @async
   * Method for fetching single one
   * @param {string} broadcasterName
   * @param {string} targetName
   * @emits CLOSE_BROADCASTER_SEARCH close broadcaster search
   * @emits SINGLE_NOTE_RESPONSE sends single note object
   * @emits SET_NEW_NOTE_VIEW sends new note view bool
   * @returns promise
   */
  getSingleUserNote = (broadcasterName, targetName, openNewNoteModal) => {
    const URL = USER_NOTES_SINGLE_NOTE__URL.replace('{broadcasterUsername}', broadcasterName).replace(
      '{targetUsername}',
      targetName
    );

    return ApiService.get(URL)
      .then((response) => {
        this.messagingService.emit(SINGLE_NOTE_RESPONSE, response.data);
        if (openNewNoteModal) {
          ModalService.showModal(ModalService.MODAL_KEYS.NEW_NOTE_MODAL, { targetName });
        } else {
          this.messagingService.emit(CLOSE_BROADCASTER_SEARCH, false);
          this.messagingService.emit(SET_NEW_NOTE_VIEW, true);
        }
      })
      .catch(({ status, data }) => {
        if (status === RESPONSE_STATUSES.STATUS_404) {
          if (data.status === 'note_not_found') {
            this.messagingService.emit(CLOSE_BROADCASTER_SEARCH, false);
            this.messagingService.emit(SET_NEW_NOTE_VIEW, true);
          } else {
            this.messagingService.emit(SET_BROADCASTER_NOTES_SEARCH_ERROR_VALUE, true);
          }
          this.messagingService.emit(SINGLE_NOTE_RESPONSE, { color: '', note: '' });
        }
      });
  };

  /**
   * @async
   * Method for fetching all notes
   * @param {string} broadcasterName
   * @emits ALL_NOTES_RESPONSE sends all notes object
   * @returns promise
   */
  getAllNotes = (broadcasterName) => {
    const URL = USER_NOTES_URL.replace('{broadcasterUsername}', broadcasterName);

    return ApiService.get(URL, { params: { offset: 0, limit: 10000 } })
      .then((response) => {
        this.messagingService.emit(ALL_NOTES_RESPONSE, response.data);
      })
      .catch((error) => parseCatch(error));
  };

  /**
   * @async
   * Method for making a new note
   * @param {string} broadcastName
   * @param {object} data
   * @emits NOTE_UPDATED_OR_CREATED update notes
   * @returns promise
   */
  makeNewNote = (broadcastName, data) => {
    const { isModalOpened, ...rest } = data;
    const URL = USER_NOTES_URL.replace('{broadcasterUsername}', broadcastName);
    ApiService.post(URL, { data: rest })
      .then(() => {
        this.messagingService.emit(UPDATE_ALL_NOTES_COUNT);
        this.messagingService.emit(UPDATE_ALL_NOTES, { peerUsername: rest.peerUsername, color: rest.color });
        this.messagingService.emit(NEW_NOTE_CREATED);
        if (isModalOpened) {
          ModalService.hideModal();
          NotificationService.create({
            type: NotificationService.type.TOAST,
            subtype: NotificationService.subtype.SUCCESS,
            text: translate('v2broadcast.note.saved'),
          });
        } else {
          this.messagingService.emit(NOTE_UPDATED_OR_CREATED, data.peerUsername);
          this.messagingService.emit(SINGLE_NOTE_RESPONSE, rest);
          this.getNotesList(broadcastName);
        }
      })
      .catch(() => {
        NotificationService.create({
          type: NotificationService.type.TOAST,
          subtype: NotificationService.subtype.ERROR,
          text: translate('account.error.something.wrong'),
        });
      });
  };

  /**
   * @async
   * Method for making a new note
   * @param {string} broadcastName
   * @param {object} data
   * @emits NOTE_UPDATED_OR_CREATED update notes
   * @returns promise
   */
  updateNote = (broadcastName, data) => {
    const { peerUsername, isSuccessViewVisible, isNewNote, isModalOpened, ...rest } = data;
    const URL = USER_NOTES_SINGLE_NOTE__URL.replace('{broadcasterUsername}', broadcastName).replace(
      '{targetUsername}',
      peerUsername
    );
    if (isNewNote) {
      this.messagingService.emit(RESET_SUCCESS_NOTE_VIEW, peerUsername.toLowerCase());
    }
    ApiService.put(URL, { data: rest })
      .then(() => {
        this.messagingService.emit(UPDATE_ALL_NOTES, { peerUsername, color: rest.color });
        if (isModalOpened) {
          ModalService.hideModal();
          NotificationService.create({
            type: NotificationService.type.TOAST,
            subtype: NotificationService.subtype.SUCCESS,
            text: translate('v2broadcast.note.saved'),
          });
        } else if (isSuccessViewVisible) {
          this.messagingService.emit(NOTE_UPDATED_OR_CREATED, peerUsername);
          if (isNewNote) {
            this.getNotesList(broadcastName);
          }
        }
      })
      .catch((error) => parseCatch(error));
  };

  /**
   * @async
   * Method for delete broadcaster note
   * @param {string} broadcasterName
   * @param {string} targetName
   * @emits NOTE_UPDATED_OR_CREATED updates notes
   * @emits BROADCASTER_NOTE_DELETED delete notes after 4 secs
   * @returns promise
   */
  removeNote = (broadcasterName, targetName, isModalOpened) => {
    const URL = USER_NOTES_SINGLE_NOTE__URL.replace('{broadcasterUsername}', broadcasterName).replace(
      '{targetUsername}',
      targetName
    );
    ApiService.delete(URL)
      .then(() => {
        this.messagingService.emit(NOTE_UPDATED_OR_CREATED, targetName);
        this.messagingService.emit(BROADCASTER_NOTE_DELETED, targetName);
        if (isModalOpened) {
          ModalService.hideModal();
          NotificationService.create({
            type: NotificationService.type.TOAST,
            subtype: NotificationService.subtype.SUCCESS,
            text: translate('v2broadcast.note.deleted'),
          });
          this.messagingService.emit(SINGLE_NOTE_RESPONSE, {});
        }
      })
      .catch((error) => parseCatch(error));
  };
}

export default new BroadcasterNotesService(createMessagingService);
